import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";

/**
 * SearchSelect
 * ─────────────────────────────────────────────────────────────────────────────
 * Select genérico con búsqueda en tiempo real (dropdown en portal). No hace
 * fetch propio — recibe las opciones ya cargadas, así sirve tanto para listas
 * de CuentaContable como de Filial o cualquier otra entidad.
 *
 * Props:
 *   options     → [{ id, primary, secondary? }] — primary y secondary son los
 *                 dos textos que se muestran por opción (ej. código y nombre).
 *   value       → id de la opción seleccionada
 *   onChange    → fn(id, option) — llamado al seleccionar
 *   placeholder → texto cuando no hay selección
 *   loading     → boolean (muestra "Cargando…" en vez del placeholder)
 *   disabled    → boolean
 *   error       → string con mensaje de error
 *   icon        → clase de ícono FontAwesome para el ancla (default fa-magnifying-glass)
 */
export function SearchSelect({
  options = [],
  value,
  onChange,
  placeholder = "Buscar…",
  loading = false,
  disabled = false,
  error,
  icon = "fa-magnifying-glass",
}) {
  const { t } = useTranslation();
  const [search,   setSearch]   = useState("");
  const [open,     setOpen]     = useState(false);
  const [rect,     setRect]     = useState(null);
  const anchorRef   = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    function handler(e) {
      const insideAnchor   = anchorRef.current?.contains(e.target);
      const insideDropdown = dropdownRef.current?.contains(e.target);
      if (!insideAnchor && !insideDropdown) setOpen(false);
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (!open) return;
    function updateRect() {
      if (anchorRef.current) setRect(anchorRef.current.getBoundingClientRect());
    }
    updateRect();
    window.addEventListener("scroll", updateRect, true);
    window.addEventListener("resize", updateRect);
    return () => {
      window.removeEventListener("scroll", updateRect, true);
      window.removeEventListener("resize", updateRect);
    };
  }, [open]);

  let dropdownTop = 0, dropdownMaxHeight = 280, dropdownWidth = 0, dropdownLeft = 0;
  if (rect) {
    const margin     = 8;
    const spaceBelow = window.innerHeight - rect.bottom - margin;
    const spaceAbove = rect.top - margin;
    if (spaceBelow >= 160 || spaceBelow >= spaceAbove) {
      dropdownMaxHeight = Math.max(120, Math.min(280, spaceBelow));
      dropdownTop = rect.bottom + 4;
    } else {
      dropdownMaxHeight = Math.max(120, Math.min(280, spaceAbove));
      dropdownTop = rect.top - dropdownMaxHeight - 4;
    }
    // El panel nunca es más angosto que ~280px, aunque el campo que lo abre
    // sea un filtro chico de grilla — si no, "código + nombre" queda ilegible
    // (ej. el nombre truncado a "Ma…"). Se ensancha hacia la derecha del
    // ancla y se recorta para no salirse de la ventana.
    const minWidth = 280;
    dropdownWidth = Math.max(rect.width, minWidth);
    dropdownLeft  = Math.min(rect.left, window.innerWidth - dropdownWidth - margin);
    dropdownLeft  = Math.max(margin, dropdownLeft);
  }
  const listMaxHeight = Math.max(80, dropdownMaxHeight - 44);

  const seleccionada = options.find((o) => o.id === value);
  const q = search.toLowerCase();
  const filtradas = options.filter(
    (o) => !q
      || String(o.primary || "").toLowerCase().includes(q)
      || String(o.secondary || "").toLowerCase().includes(q)
  );

  function seleccionar(opt) {
    onChange(opt.id, opt);
    setSearch("");
    setOpen(false);
  }

  return (
    <div ref={anchorRef} style={{ position: "relative" }}>
      <div
        style={{
          display: "flex", alignItems: "center",
          background: "var(--bg-input)", border: `1px solid ${error ? "#ef4444" : "var(--border-card)"}`,
          borderRadius: 6, padding: "8px 10px", cursor: disabled ? "not-allowed" : "pointer",
          gap: 8,
        }}
        onClick={() => { if (!disabled) setOpen((v) => !v); }}
      >
        <i className={`fa-solid ${icon}`} style={{ color: "var(--text-muted)", fontSize: "0.75rem" }} />
        <span style={{
          flex: 1, minWidth: 0, fontSize: "0.85rem",
          color: seleccionada ? "var(--text-primary)" : "var(--text-muted)",
          whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
        }} title={seleccionada ? [seleccionada.primary, seleccionada.secondary].filter(Boolean).join(" — ") : undefined}>
          {seleccionada
            ? [seleccionada.primary, seleccionada.secondary].filter(Boolean).join(" — ")
            : loading ? t("common.cargando") : placeholder}
        </span>
        <i className={`fa-solid fa-chevron-${open ? "up" : "down"}`}
          style={{ color: "var(--text-muted)", fontSize: "0.65rem" }} />
      </div>

      {open && rect && createPortal(
        <div
          ref={dropdownRef}
          style={{
            position: "fixed", top: dropdownTop, left: dropdownLeft, width: dropdownWidth,
            background: "var(--bg-card)", border: "1px solid var(--border-card)",
            borderRadius: 8, zIndex: 10050, overflow: "hidden",
            boxShadow: "0 8px 24px rgba(0,0,0,0.4)", maxHeight: dropdownMaxHeight,
          }}
        >
          <div style={{
            padding: "8px 10px", borderBottom: "1px solid var(--border-card)",
            display: "flex", alignItems: "center", gap: 7,
          }}>
            <i className="fa-solid fa-magnifying-glass"
              style={{ color: "var(--text-muted)", fontSize: "0.75rem" }} />
            <input
              autoFocus
              type="text"
              placeholder={t("catalogo.buscarPlaceholder")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              style={{
                flex: 1, background: "none", border: "none",
                color: "var(--text-primary)", fontSize: "0.83rem", outline: "none",
              }}
            />
          </div>

          <div style={{ overflowY: "auto", maxHeight: listMaxHeight }}>
            {filtradas.length === 0 ? (
              <div style={{ padding: "12px 14px", color: "var(--text-muted)", fontSize: "0.82rem" }}>
                {search ? t("catalogo.sinResultados") : t("catalogo.sinCuentas")}
              </div>
            ) : (
              filtradas.map((o) => (
                <button
                  key={o.id}
                  type="button"
                  onClick={() => seleccionar(o)}
                  style={{
                    display: "flex", alignItems: "center", gap: 10,
                    width: "100%", padding: "9px 14px",
                    background: o.id === value ? "rgba(249,115,22,0.12)" : "transparent",
                    border: "none", textAlign: "left", cursor: "pointer",
                    borderBottom: "1px solid var(--border-card)",
                    transition: "background 0.1s",
                  }}
                  onMouseOver={(e) => {
                    if (o.id !== value) e.currentTarget.style.background = "var(--hover-bg-strong)";
                  }}
                  onMouseOut={(e) => {
                    if (o.id !== value) e.currentTarget.style.background = "transparent";
                  }}
                >
                  {o.primary && (
                    <span style={{
                      fontFamily: "monospace", fontSize: "0.75rem",
                      color: "#f97316", minWidth: o.secondary ? 110 : "auto",
                    }}>
                      {o.primary}
                    </span>
                  )}
                  {o.secondary && (
                    <span style={{
                      color: "var(--text-primary)", fontSize: "0.83rem", flex: 1, minWidth: 0,
                      whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                    }} title={o.secondary}>
                      {o.secondary}
                    </span>
                  )}
                  {o.id === value && (
                    <i className="fa-solid fa-check" style={{ color: "#f97316", fontSize: "0.7rem" }} />
                  )}
                </button>
              ))
            )}
          </div>
        </div>,
        document.body
      )}

      {error && (
        <div style={{
          color: "#f87171", fontSize: "0.75rem",
          marginTop: 4, display: "flex", alignItems: "center", gap: 4,
        }}>
          <i className="fa-solid fa-circle-exclamation" style={{ fontSize: "0.7rem" }} />
          {error}
        </div>
      )}
    </div>
  );
}
